import type { ReactNode } from "react";
import type { FieldDef } from "../../lib/field-util";
import { NoBackend } from "../common/NoBackend";
import { BoxHeader } from "./BoxHeader";
import { AddForm } from "./AddForm";
import type { AddFormPayload } from "./AddForm";

// Standard section: box header with optional right slot, box wrapper, and a
// unified empty-state. When `onAdd` is set on an empty section, the empty
// state becomes an interactive "+" add box (for the user's own editable
// profile). Without `onAdd`, an empty section shows the NoBackend marker.

interface SectionProps {
  /**
   * Anker-Präfix des Abschnitts; sein Anlege-Formular leitet daraus ab. Ohne ihn
   * bleibt das Formular ankerlos — mehrere Abschnitte können gleichzeitig eines
   * offen haben, eine feste Kennung gäbe es dann mehrfach.
   */
  id?: string;
  title?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  empty?: boolean;
  emptyLabel?: string;
  emptyHint?: string;
  // `onAdd` and `addFields` are paired — pass both to enable the add-form
  // branch, or neither to render NoBackend in the empty state.
  onAdd?: (payload: AddFormPayload) => void | Promise<void>;
  addFields?: readonly FieldDef[];
  addLabel?: string;
  /** Open the add form immediately on mount (deep-link from a quick action). */
  addInitiallyOpen?: boolean;
  /** Passed to AddForm: off when the section governs visibility with one master for the box. */
  addShareControl?: boolean;
  box?: boolean;
  className?: string;
  // Draw attention to the whole section (e.g. it has pending LinkedIn-import
  // changes): an accent ring around the box. No layout shift (ring is a shadow).
  highlight?: boolean;
}

export function Section({
  id,
  title,
  right,
  children,
  empty = false,
  emptyLabel = "Keine Daten",
  emptyHint,
  onAdd,
  addFields,
  addLabel,
  addInitiallyOpen = false,
  addShareControl = true,
  box = true,
  className = "",
  highlight = false,
}: SectionProps) {
  const header = title != null || right != null ? <BoxHeader title={title} right={right} /> : null;
  // The add form keeps one tree position whether the section is empty or not: a section can flip
  // while the form is open (first entry saved, last one removed), and a remount lost its input (spark#315).
  const addForm =
    onAdd && addFields ? (
      <AddForm
        id={id ? `${id}-add` : undefined}
        variant={empty ? "empty" : "row"}
        onAdd={onAdd}
        fields={addFields}
        label={addLabel}
        initiallyOpen={addInitiallyOpen}
        shareControl={addShareControl}
      />
    ) : null;

  const emptyNotice = addForm ? null : <NoBackend label={emptyLabel} hint={emptyHint} />;
  const body = (
    <>
      {empty ? emptyNotice : children}
      {addForm}
    </>
  );

  return (
    <div id={id} className={className}>
      {box ? (
        <div className={`box bg-surface p-5 ${highlight ? "ring-2 ring-accent-decoration/40" : ""}`}>
          {header}
          {body}
        </div>
      ) : (
        <>
          {header}
          {body}
        </>
      )}
    </div>
  );
}
